import React, {createContext, Dispatch, lazy, Suspense, useCallback, useContext, useEffect, useState} from "react";
import {Redirect, Route, Switch, useLocation} from "react-router-dom";

import UserAPIs from "../apis/user.apis";

import {CustomLoader} from "../components/Spinner/CustomLoader";
import CommonApis from "../apis/common.apis";
import useIsMounted from "ismounted";
import NavBar from "../components/navigation/navbar";
import SearchBox from "../components/searchbox/searchBox";
import Footer from "../components/footer/footer";
import BaseAPIs from "../apis/base.apis";
import StickyHelp from "../components/StickyHelp/StickyHelp";
import {AppDispatchContext, AppStateContext} from "../App";
import {iStoreAction} from "../reducer";
import ReactGA from "react-ga";
import CovidAlert from "../components/WebsiteTopAlert/CovidAlert";
import OrderApis from "../apis/order.apis";
import {Container} from "react-bootstrap";
import TagManager from "react-gtm-module";
import {useToasts} from "react-toast-notifications";
import ReactPixel from 'react-facebook-pixel';
import {HideGuideBot, showGuideBot} from "../helper/common";
import {hotjar} from 'react-hotjar';
import useInterval from "use-interval";

const HomePage = lazy(() => import('./HomePage/HomePage'));
const HomePage2 = lazy(() => import('./HomePage/HomePage2'));
const ComingSoon = lazy(() => import('./HomePage/ComingSoon/ComingSoon'));
const GettingStartedPage = lazy(() => import('./Registration/GettingStartedPage'));
const ForgotPasswordPage = lazy(() => import('./Registration/ForgotPasswordPage'));
const LoginPage = lazy(() => import('./Registration/LoginPage'));
const ResetPassword = lazy(() => import('./Registration/ResetPassword'));
const SetPassword = lazy(() => import('./Registration/SetPassword'));
const EmailVerification = lazy(() => import('./Registration/EmailVerification'));
const ResendVerification = lazy(() => import('./Registration/ResendVerification'));
const Registration = lazy(() => import('./Registration/Registration'));
const PackageDetailPage2 = lazy(() => import('./StaticPages/PackageDetail/PackageDetailPage'));
const MarketingServiceBasicAddon = lazy(() => import('./StaticPages/PackageDetail/MarketingServiceBasicAddon'));
const MarketingServiceBasic = lazy(() => import('./StaticPages/PackageDetail/MarketingServiceBasic'));
const MarketingServiceForeign = lazy(() => import('./StaticPages/PackageDetail/MarketingServiceForeign'));
const MarketingServicePlus = lazy(() => import('./StaticPages/PackageDetail/MarketingServicePlus'));
const PublishingWorkflowV2 = lazy(() => import('./StaticPages/PublishingWorkingV2'));
const AboutSage = lazy(() => import('./StaticPages/AboutSage'));
const FAQPage = lazy(() => import('./StaticPages/FAQ'));
const UnderConstruction1 = lazy(() => import('./StaticPages/UnderConstruction/UnderConstruction1'));
const UnderConstruction2 = lazy(() => import('./StaticPages/UnderConstruction/UnderConstruction2'));
const UnderConstruction3 = lazy(() => import('./StaticPages/UnderConstruction/UnderConstruction3'));
const PrivacyPolicyPage = lazy(() => import('./StaticPages/PrivacyPolicy'));
const TermsPage = lazy(() => import('./StaticPages/Terms'));
const ProductList = lazy(() => import('./Ecommerce/ProductList'));
const SingleSeriesPage = lazy(() => import('./Ecommerce/Series/Single.Series.Page'));
const CookiesPage = lazy(() => import('./StaticPages/Cookies'));
const ContactUsPage = lazy(() => import('./Registration/ContactUsPage'));
const DashboardRoutes = lazy(() => import('./Dashboard/Dashboard.Routes'));
const PaymentHandlePage = lazy(() => import('./Payment/PaymentHandlePage'));
const OrderPaymentHandlePage = lazy(() => import('./Payment/OrderPaymentHandlePage'));
const GuestOrderPaymentHandlePage = lazy(() => import('./Payment/GuestOrderPaymentHandlePage'));
const GuestOrderPaymentHandlePageByLink = lazy(() => import('./Payment/GuestOrderPaymentHandlePageByLink'));
const ManuScriptGuideline = lazy(() => import('./StaticPages/ManuscriptGuidelines/ManuScriptGuideLine.Router'));
const SingleProductPage = lazy(() => import("./Ecommerce/SingleProductPage"));
const ShoppingCart = lazy(() => import("./Ecommerce/ShoppingCart/ShoppingCart"));
const CheckoutGuest = lazy(() => import("./Ecommerce/CheckoutGuest/CheckoutGuest"));
const GuestCheckForm = lazy(() => import("./Ecommerce/GuestCheckoutForm/GuestCheckForm"));
const GuestOrderReview = lazy(() => import("./Ecommerce/GuestOrderReview/GuestOrderReview"));
const OrderReview = lazy(() => import("./Ecommerce/OrderReview/OrderReview"));


export interface iStoreFunctions {
    add_to_cart?: (product_id: number, quantity: number, product_variant_id: number,
                   add_to_cart?: boolean, allow_foreign?: boolean, callback?: any) => void
}

export const AppCartContext = createContext<iStoreFunctions>({});

function Router() {
    const dispatch: Dispatch<iStoreAction> = useContext(AppDispatchContext);
    const {ip_country} = useContext(AppStateContext);
    const isMounted = useIsMounted();
    const [loading, setLoading] = useState(true);

    const {loggedInUser,added_to_cart} = useContext(AppStateContext);
    const tagManagerArgs = {
        gtmId: 'GTM-K9CSBH2'
    }
    const onFetchUser = useCallback(() => {
        setLoading(true);
        new UserAPIs().fetch_user().then((response) => {
            if (isMounted.current) {
                if (UserAPIs.hasError(response)) {
                    setLoading(false);
                } else {
                    dispatch({type: "set_logged_in_user", loggedInUser: {...response}})
                    setLoading(false);
                }
            }
        });
    }, [isMounted, dispatch]);


    const location = useLocation();

    useEffect(() => {
        if (
            location.pathname === "" ||
            location.pathname === "/" ||
            location.pathname === "/journals"
        ) {
            HideGuideBot()
        } else {
            showGuideBot()
        }
    }, [location])

    const {addToast, removeAllToasts} = useToasts()

    useInterval(()=>{
        let _added_to_cart=[...added_to_cart];
        _added_to_cart=_added_to_cart.map(value => {
            if(value.added_at<0){
                return {...value};
            }
            return {...value,added_at:value.added_at-1}
        });
        let a=_added_to_cart.filter(value => {
            return value.added_at>0
        });
        if(a.length>0 || added_to_cart.length>0){
            dispatch({type: "added_to_cart", added_to_cart: a});
        }
    },1000)

    const loadCart = useCallback((product_id) => {
        dispatch({type: "updating_cart", updating_cart: product_id});
        dispatch({type: "added_to_cart", added_to_cart: [...added_to_cart,{
                product_id:product_id,
                added_at:3}]});

        new OrderApis().load_cart().then((res) => {
            if (isMounted.current) {
                if (OrderApis.hasError(res, addToast, removeAllToasts)) {
                    dispatch({type: "updating_cart", updating_cart: 0});
                } else {
                    dispatch({type: "cart", cart: res, updating_cart: 0})
                }
            }
        })
    }, [isMounted])

    const add_to_cart = useCallback((product_id: number, quantity: number, product_variant_id: number,
                                     add_to_cart?: boolean, allow_foreign?: boolean, callback?: any) => {
        dispatch({type: "updating_cart", updating_cart: product_id});
        new OrderApis()
            .add_to_cart(product_id, quantity, product_variant_id, add_to_cart, allow_foreign).then((res) => {
            if (isMounted.current) {
                removeAllToasts();
                if (OrderApis.hasError(res, addToast, removeAllToasts)) {
                    dispatch({type: "updating_cart", updating_cart: 0,});

                } else {
                    loadCart(product_id);
                    callback && callback()
                }
            }
        })
    }, [])


    useEffect(() => {
        TagManager.initialize(tagManagerArgs)
        if (process.env.REACT_APP_PIXEL_ID) {
            ReactPixel.init(process.env.REACT_APP_PIXEL_ID);
        }

        if (
            !location.pathname.includes("reset_password") &&
            !location.pathname.includes("set_password") &&
            !location.pathname.includes("verify_email")
        ) {
            if (process.env.REACT_APP_PIXEL_ID) {
                ReactPixel.pageView();
            }
            ReactGA.pageview(location.pathname + location.search);
        }
    }, [location])

    useEffect(() => {
        if (process.env.REACT_APP_HOTJAR_HJID && process.env.REACT_APP_HOTJAR_HJSV) {
            hotjar.initialize(parseInt(process.env.REACT_APP_HOTJAR_HJID), parseInt(process.env.REACT_APP_HOTJAR_HJSV));
        }
    }, [])

    useEffect(() => {
        onFetchUser();
    }, [])

    useEffect(() => {
        const handleInvalidToken = (e: any) => {
            if (e.key === 'loggedInUser_spectrum' && e.oldValue && !e.newValue) {
                window.location.reload();
            }
        }
        window.addEventListener('storage', handleInvalidToken)
        return function cleanup() {
            window.removeEventListener('storage', handleInvalidToken)
        }
    }, [])

    useEffect(() => {
        loadCart(0);
        new CommonApis().getAllMasters().then((res) => {
            if (isMounted.current) {
                if (!res || CommonApis.hasError(res)) {
                    dispatch({
                        type: "masters_not_loaded",
                        masters_not_loaded: "There is some error loading all the resources"
                    });
                } else {
                    dispatch({type: "masters", masters: res});
                }
            }
        });
        if (!ip_country) {
            new BaseAPIs().ipLocation().then((res) => {
                if (!BaseAPIs.hasError(res)) {
                    dispatch({type: "ip_country", ip_country: res.country});
                } else {
                    dispatch({
                        type: "ip_country", ip_country: {
                            alpha_2: "IN",
                            alpha_3: "IND",
                            id: 102,
                            isd_code: "+91",
                            name: "India",
                            numeric: "356",
                            pricing_region_id: 4,
                            created_at: "",
                            updated_at: ""
                        }
                    });
                }
            })
        }

    }, [isMounted, loggedInUser]);

    if (loading) {
        return <CustomLoader/>;
    }
    return <AppCartContext.Provider value={{add_to_cart: add_to_cart}}>
        <Suspense fallback={<CustomLoader/>}>
            <Switch>
                <WithoutAuthRoute path="/getting-started">
                    <GettingStartedPage/>
                </WithoutAuthRoute>
                <WithoutAuthRoute path="/registration">
                    <Registration/>
                </WithoutAuthRoute>
                <WithoutAuthRoute path="/login"><LoginPage/></WithoutAuthRoute>
                <WithoutAuthRoute path="/forgot-password"><ForgotPasswordPage/></WithoutAuthRoute>
                <WithoutAuthRoute path="/reset_password/:token/:email"><ResetPassword/></WithoutAuthRoute>
                <WithoutAuthRoute path="/set_password/:token/:email"><SetPassword/></WithoutAuthRoute>
                <WithoutAuthRoute path="/verify_email/:token"><EmailVerification/></WithoutAuthRoute>
                <WithoutAuthRoute path="/resend-verification"><ResendVerification/></WithoutAuthRoute>
                <AppRoute path="/payment-response/checkout/:id">
                    <Container>
                        <OrderPaymentHandlePage/>
                    </Container>
                </AppRoute>
                <AppRoute path="/payment-response/guest-checkout/:id">
                    <Container>
                        <GuestOrderPaymentHandlePage/>
                    </Container>
                </AppRoute>
                <WithoutAuthRoute path="/cart/checkout/guest/continue">
                    <GuestCheckForm/>
                </WithoutAuthRoute>
                <WithoutAuthRoute path="/cart/checkout/guest/order/review/:id">
                    <Container>
                        <GuestOrderPaymentHandlePageByLink/>
                    </Container>
                </WithoutAuthRoute>
                <WithoutAuthRoute path="/cart/checkout/guest/order/review">
                    <GuestOrderReview/>
                </WithoutAuthRoute>


                <WithoutAuthRoute path="/cart/checkout/guest">
                    <Container>
                        <GuestCheckForm/>
                    </Container>
                </WithoutAuthRoute>
                <AppRoute path="/cart/checkout">
                    <Container>
                        <OrderReview/>
                    </Container>
                </AppRoute>

                <AppRoute path="/cart">
                    <Container><ShoppingCart/></Container></AppRoute>
                <PrivateRoute path="/dashboard"><DashboardRoutes/></PrivateRoute>
                {/* <IndianUserPrivateRoute
                    path="/package-detail/marketing/plus"><MarketingServicePlus/></IndianUserPrivateRoute>
                <IndianUserPrivateRoute
                    path="/package-detail/marketing/basic-addon"><MarketingServiceBasicAddon/></IndianUserPrivateRoute>
                <IndianUserPrivateRoute
                    path="/package-detail/marketing/basic"><MarketingServiceBasic/></IndianUserPrivateRoute>
                <NotIndianUserPrivateRoute
                    path="/package-detail/marketing/service"><MarketingServiceForeign/></NotIndianUserPrivateRoute>
                <PrivateRoute path="/package-detail"><PackageDetailPage2/></PrivateRoute> */}
                <AppRoute path="/publishing-workflow"><PublishingWorkflowV2/></AppRoute>
                <AppRoute path="/payment-response/:type/:proposal_id"><PaymentHandlePage/></AppRoute>
                <AppRoute path="/faq"><FAQPage/></AppRoute>
                <AppRoute path="/books" showSearch><ProductList/></AppRoute>
                <AppRoute path="/series/:seo_name"><SingleSeriesPage/></AppRoute>
                <AppRoute path="/book/:seo_name/:id"><SingleProductPage/></AppRoute>
                {/* <AppRoute path="/privacy-policy"><PrivacyPolicyPage/></AppRoute> */}
                <AppRoute path="/about"><AboutSage/></AppRoute>
                <AppRoute path="/manuscript-guidelines"><ManuScriptGuideline/></AppRoute>
                <AppRoute path="/terms-and-conditions"><TermsPage/></AppRoute>
                <AppRoute path="/cookie-policy"><CookiesPage/></AppRoute>
                <AppRoute path="/contact-us"><ContactUsPage/></AppRoute>
                <AppRoute path="/journals" noHeader><ComingSoon/></AppRoute>
                <AppRoute path="/home"><AboutSage/></AppRoute>
                <AppRoute path="/"><AboutSage/></AppRoute>
                <Redirect from="/" to="/"/>
            </Switch>
        </Suspense>
        <Footer/>
    </AppCartContext.Provider>;
}

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
function PrivateRoute({children, noHeader, ...rest}: any) {
    const {loggedInUser} = useContext(AppStateContext);
    return (<>
            {
                !noHeader && <>
                    <CovidAlert/>
                    <NavBar/>
                    {/* <SearchBox/> */}

                    {loggedInUser && loggedInUser.author && <StickyHelp/>}
                </>
            }
            <Route
                {...rest}
                render={({location}) =>
                    loggedInUser ? (
                        children
                    ) : (
                        <Redirect
                            to={{
                                pathname: "/login",
                                state: {from: location}
                            }}
                        />
                    )
                }
            />
        </>
    );
}


// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
function AppRoute({children, noHeader, showSearch, ...rest}: any) {
    const {loggedInUser} = useContext(AppStateContext);
    return (<>
            {
                !noHeader && <>
                    <CovidAlert/>
                    <NavBar/>
                    {/* <SearchBox/> */}
                    {
                        !!showSearch && <SearchBox/>
                    }

                    {loggedInUser && loggedInUser.author && <StickyHelp/>}
                </>
            }
            <Route
                {...rest}
                render={() => {
                    return children
                }}
            />
        </>
    );
}

function IndianUserPrivateRoute({children, noHeader, ...rest}: any) {
    const {loggedInUser} = useContext(AppStateContext);
    return (<>
            {
                !noHeader && <>
                    <CovidAlert/>
                    <NavBar/>
                    {/* <SearchBox/> */}

                    {loggedInUser && loggedInUser.author && <StickyHelp/>}
                </>
            }
            <Route
                {...rest}
                render={({location}) =>
                    (loggedInUser
                        && loggedInUser.pricing_region &&
                        loggedInUser.pricing_region.code === "india"
                    ) ? (
                        children
                    ) : <Redirect
                        to={{
                            pathname: "/home"
                        }}
                    />

                }
            />
        </>
    );
}

function NotIndianUserPrivateRoute({children, noHeader, ...rest}: any) {
    const {loggedInUser} = useContext(AppStateContext);
    return (<>
            {
                !noHeader && <>
                    <CovidAlert/>
                    <NavBar/>
                    {/* <SearchBox/> */}

                    {loggedInUser && loggedInUser.author && <StickyHelp/>}
                </>
            }
            <Route
                {...rest}
                render={({location}) =>
                    (loggedInUser
                        && loggedInUser.pricing_region &&
                        loggedInUser.pricing_region.code !== "india"
                    ) ? (
                        children
                    ) : <Redirect
                        to={{
                            pathname: "/home"
                        }}
                    />

                }
            />
        </>
    );
}

function WithoutAuthRoute({children, noHeader, ...rest}: any) {
    const {loggedInUser} = useContext(AppStateContext);
    return (<>
            {
                !noHeader && <>
                    <CovidAlert/>
                    <NavBar/>
                    {/* <SearchBox/> */}

                    {loggedInUser && loggedInUser.author && <StickyHelp/>}
                </>
            }
            <Route
                {...rest}
                render={({location}) =>
                    !loggedInUser ? (
                        children
                    ) : (
                        <Redirect
                            to={{
                                pathname: "/home",
                                state: {from: location}
                            }}
                        />
                    )
                }
            />
        </>
    );
}

export default Router;
